import {
  Component,
  ElementRef,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonIcon,
  IonInput,
  IonRow,
  IonSpinner,
} from '@ionic/angular/standalone';

/**
 * Typ for a chat message.
 * @interface ChatMessage
 * @property {string} sender - The sender of the message, either 'bot' or 'user'.
 * @property {string} message - The message text.
 * @property {boolean} [error] - Whether the message is an error message.
 */

const headers = new HttpHeaders().set('Content-Type', 'application/json');

interface ChatMessage {
  sender: 'bot' | 'user';
  message: string;
  error?: boolean;
  isUrl?: boolean;
}

/**
 * Component for the chatbot assistance.
 */
@Component({
  selector: 'app-chatbot-assistance',
  templateUrl: './chatbot-assistance.component.html',
  styleUrls: ['./chatbot-assistance.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    IonContent,
    IonCard,
    IonCardHeader,
    IonGrid,
    IonRow,
    IonCol,
    IonIcon,
    IonButton,
    IonCardContent,
    IonSpinner,
    IonFooter,
    IonInput,
    NgClass,
    NgForOf,
    NgIf,
  ],
})
export class ChatbotAssistanceComponent implements OnInit {
  messages: ChatMessage[] = [
    { sender: 'bot', message: 'Hallo! Wie kann ich Ihnen helfen?' },
  ];
  userInput: string = '';
  chatVisible: boolean = false;
  isLoading = false;
  /**
   * reference to the messages container element
   * @private @type {ElementRef}
   * @memberof ChatbotAssistanceComponent
   * @default null
   * @example null
   */
  @ViewChild('messagesContainer', { static: false })
  private messagesContainer: ElementRef;

  constructor(
    private http: HttpClient,
    private renderer: Renderer2,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {}

  /**
   * Toggles the chatbot visibility.
   * @memberof ChatbotAssistanceComponent
   * @returns {void}
   */
  toggleChatbot() {
    this.chatVisible = !this.chatVisible;
    if (this.chatVisible) {
      this.renderer.addClass(document.body, 'chatbot-open');
    }
  }

  /**
   * Sends the user message to the chatbot and adds the response to the chat.
   * @memberof ChatbotAssistanceComponent
   * @returns {void}
   */
  sendChatbotMessage() {
    const trimmedMessage = this.userInput.trim();
    if (!trimmedMessage) {
      return;
    }
    // Benutzer-Nachricht hinzufügen
    this.messages.push({
      sender: 'user',
      message: trimmedMessage,
    });
    this.scrollToBottom();
    this.userInput = '';
    this.isLoading = true;

    const payload = {
      message: trimmedMessage,
    };

    /**
     * Send the user message to the chatbot and add the response to the chat.
     */
    this.http
      .post<any>('http://127.0.0.1:7863/chat', payload, {
        headers: headers,
      })
      .subscribe({
        next: data => {
          this.isLoading = false;
          const botText =
            typeof data.message === 'object'
              ? JSON.stringify(data.message, null, 2) // Formatieren, falls es ein Objekt ist
              : data?.message || 'Entschuldigung, keine Antwort verfügbar.';
          // Prüfen, ob die Antwort eine URL ist (fängt mit http oder https an)
          const isUrl =
            botText.startsWith('http://') || botText.startsWith('https://');
          let safeUrl: SafeUrl;
          if (isUrl) {
            safeUrl = this.sanitizer.bypassSecurityTrustUrl(botText);
          }
          this.messages.push({
            sender: 'bot',
            message: isUrl ? safeUrl : botText,
            isUrl: isUrl, // Speichert, ob es sich um eine URL handelt
          });
          this.scrollToBottom();
        },
        error: error => {
          console.error('Fehler:', error);
          this.isLoading = false;
          const errorMessage =
            error.message ?? 'Entschuldigung, ein Fehler ist aufgetreten.';
          this.messages.push({
            sender: 'bot',
            message: errorMessage,
            error: true,
          });
          this.scrollToBottom();
        },
      });
  }

  /**
   * Scrolls to the bottom of the messages' container.
   * @memberof ChatbotAssistanceComponent
   * @returns {void}
   */
  scrollToBottom(): void {
    requestAnimationFrame(() => {
      const lastMessage = this.messagesContainer.nativeElement.lastElementChild;
      if (lastMessage)
        lastMessage.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }

  /**
   * Clears the chat messages.
   * @memberof ChatbotAssistanceComponent
   * @returns {void}
   */
  async reloadChatbot(): Promise<void> {
    this.messages = [
      { sender: 'bot', message: 'Hallo! Wie kann ich Ihnen helfen?' },
    ];
    this.scrollToBottom(); // Scrollt nach oben (optional)
    this.isLoading = false; // Setzt den Ladezustand zurück
    this.userInput = ''; // Setzt die Benutzereingabe zurück
  }
}
